import React from 'react';

const PrivacyPolicy = () => {
  return (
    <div style={styles.page}>
      <div style={styles.content}>
        <h1>Privacy Policy</h1>
        <p style={styles.updated}>Last updated: July 2026</p>

        <h2>1. Who we are</h2>
        <p>
          This website is operated by <strong>ANY-Web Ltd.</strong>, a company registered in the United Kingdom.
          For privacy enquiries contact: <a href="https://www.any-web.uk/legal_privacy.aspx">any-web.uk/legal_privacy</a>
        </p>

        <h2>2. What data we process and why</h2>

        <h3>2.1 Server logs (legitimate interest)</h3>
        <p>
          When you visit this website, our servers automatically receive and log your IP address, browser
          type, referring page, and the time of your visit. This information is processed on the basis of
          our <strong>legitimate interest</strong> in operating and securing the service. Log data is retained
          for up to 30 days and is not shared with third parties for marketing purposes.
        </p>
        <p>
          Our infrastructure makes requests to the following domains, which receive your IP address as
          part of normal HTTP traffic:
        </p>
        <ul>
          <li><code>any-dns.co.uk</code> — serves page content and site configuration</li>
          <li><code>tablesdirect.co.uk</code> — serves site stylesheets</li>
        </ul>
        <p>Both domains are operated by ANY-Web Ltd.</p>

        <h3>2.2 Search terms (legitimate interest)</h3>
        <p>
          When you use the search box on this website we record the words you searched for, the
          address of this site, the page you searched from, and a <strong>temporary pseudonymous
          identifier</strong> derived from your IP address. That identifier changes every day and
          cannot be reversed to reveal your IP address, so we cannot identify you from this data or
          follow you from one day to the next. Your IP address itself is never stored with your search.
        </p>
        <p>
          We do this on the basis of our <strong>legitimate interest</strong> in understanding what
          visitors look for, so we can improve the content of this site and our other village websites.
          Search records are automatically deleted after <strong>90 days</strong>. They are not shared
          with third parties and are not used for advertising or profiling. Because the data cannot be
          linked back to you, we are unable to locate and extract any one person's searches; you can
          object to this processing via the contact route in section 4.
        </p>
        <p>
          If you choose the &ldquo;search the web on DuckDuckGo&rdquo; option, your search term is sent
          to DuckDuckGo and handled under{' '}
          <a href="https://duckduckgo.com/privacy" target="_blank" rel="noopener noreferrer">their privacy policy</a>.
          Nothing is sent to any search engine unless you actively choose that option.
        </p>

        <h3>2.3 Browser local storage (strictly necessary / functional)</h3>
        <p>
          This website stores small amounts of data in your browser's local storage to make the site
          function correctly. See our <a href="/cookie-policy">Cookie &amp; Storage Policy</a> for full
          details. No personal data about you is stored in local storage — only site configuration,
          your language preference, and any display preferences you choose (theme, light/dark
          appearance, accent colour). These stay in your browser and are never sent to our servers.
        </p>

        <h3>2.4 Tours &amp; attractions (Viator)</h3>
        <p>
          Some pages show a <strong>Viator</strong> (TripAdvisor company) attractions banner. The
          banner is a simple image with a link: displaying it loads the image from Viator's content
          network (<code>partners.vtrcdn.com</code>), which receives your IP address as part of normal
          web traffic. It sets <strong>no cookies</strong> and does no tracking on this site. If you
          click it, you go to viator.com, where the{' '}
          <a href="https://www.viator.com/support/privacyPolicy" target="_blank" rel="noopener noreferrer">Viator privacy policy</a>{' '}
          applies.
        </p>
        <p>
          Some pages also offer a fuller tours-and-attractions <strong>listing</strong>. That does{' '}
          <strong>not</strong> load automatically: it only loads if you press the &ldquo;Show
          attractions&rdquo; button. If you do, your browser connects to Viator, which receives your
          IP address and may set its own cookies for functionality and affiliate attribution, handled
          under the Viator privacy policy. Your choice is remembered in local storage so the listing
          loads automatically on later visits; clearing local storage withdraws it.
        </p>

        <h3>2.5 Fonts (self-hosted)</h3>
        <p>
          This website uses the Montserrat typeface, served directly from our own infrastructure.
          No request is made to Google Fonts or any third-party font provider.
        </p>

        <h2>3. Cookies</h2>
        <p>
          This website does <strong>not</strong> set any HTTP cookies. We do not use advertising cookies,
          tracking pixels, or analytics services. See our{' '}
          <a href="/cookie-policy">Cookie &amp; Storage Policy</a> for details on local storage use.
        </p>

        <h2>4. Your rights under UK GDPR / EU GDPR</h2>
        <p>You have the right to:</p>
        <ul>
          <li>Access the personal data we hold about you</li>
          <li>Request correction of inaccurate data</li>
          <li>Request erasure of your data</li>
          <li>Object to processing based on legitimate interest</li>
          <li>Lodge a complaint with the ICO (UK) or your national supervisory authority (EU)</li>
        </ul>
        <p>
          To exercise your rights, contact us via{' '}
          <a href="https://www.any-web.uk/legal_privacy.aspx">any-web.uk/legal_privacy</a>.
        </p>

        <h2>5. Data retention</h2>
        <p>
          Server logs are retained for up to 30 days. Search records are retained for up to 90 days and
          then deleted automatically. Local storage data persists until cleared by the user.
        </p>

        <h2>6. Changes to this policy</h2>
        <p>
          We may update this policy. When we do, the "Last updated" date above will change.
          Continued use of the site after changes constitutes acceptance of the revised policy.
        </p>
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    padding: '40px 16px',
    background: '#fff',
  },
  content: {
    maxWidth: '760px',
    margin: '0 auto',
    fontFamily: 'Montserrat, sans-serif',
    color: '#1a1b1f',
    lineHeight: '1.7',
  },
  updated: {
    color: '#666',
    fontSize: '14px',
    marginBottom: '32px',
  },
};

export default PrivacyPolicy;
